import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {BacnetDeviceId, CollectorId, DeviceId} from "@shared/util/app-types";
import {
  CollectorCommandResult,
  CollectorCommandService
} from "@shared/services/api/collector-command.service";

export class DiscoverDevicesExecution {
  collectorId: CollectorId;
  commandResult: Observable<CollectorCommandResult>;
}

export class DiscoverDeviceByIdExecution {
  collectorId: CollectorId;
  bacnetDeviceId: BacnetDeviceId;
  commandResult: Observable<CollectorCommandResult>;
}

export class GetAllDeviceObjectPropertiesExecution {
  collectorId: CollectorId;
  deviceId: DeviceId;
  commandResult: Observable<CollectorCommandResult>;
}

@Injectable({
  providedIn: 'root'
})
export class BacerScanActionsService {

  private readonly logger: AceLogger;

  private readonly discoverDevicesExecutions: Map<CollectorId, DiscoverDevicesExecution> = new Map();
  private readonly discoverDeviceByIdExecutions: Map<CollectorId, DiscoverDeviceByIdExecution> = new Map();
  private readonly getAllDeviceObjectPropertiesExecutions: Map<DeviceId, GetAllDeviceObjectPropertiesExecution> = new Map();

  constructor(private collectorCommandService: CollectorCommandService,
              logService: AceLoggerService) {
    this.logger = logService.create(BacerScanActionsService.name);
  }

  public findDiscoverDevicesExecution(collectorId: CollectorId): DiscoverDevicesExecution | null {
    return this.discoverDevicesExecutions.get(collectorId);
  }

  public deleteDiscoverDevicesExecution(collectorId: CollectorId): void {
    this.discoverDevicesExecutions.delete(collectorId);
  }

  public findDiscoverDeviceByIdExecution(collectorId: CollectorId): DiscoverDeviceByIdExecution | null {
    return this.discoverDeviceByIdExecutions.get(collectorId);
  }

  public deleteDiscoverDeviceByIdExecution(collectorId: CollectorId): void {
    this.discoverDeviceByIdExecutions.delete(collectorId);
  }

  public findGetAllDeviceObjectPropertiesExecutions(collectorId: CollectorId): Array<GetAllDeviceObjectPropertiesExecution> {
    const values = Array.from(this.getAllDeviceObjectPropertiesExecutions.values());
    return values.filter((execution) => execution.collectorId == collectorId);
  }

  public deleteGetAllDeviceObjectPropertiesExecution(deviceId: DeviceId): void {
    this.getAllDeviceObjectPropertiesExecutions.delete(deviceId);
  }

  public discoverDevices(collectorId: CollectorId): DiscoverDevicesExecution {

    const existingExecution = this.findDiscoverDevicesExecution(collectorId);
    if (existingExecution) {
      return existingExecution;
    }

    const newExecution: DiscoverDevicesExecution = {
      collectorId: collectorId,
      commandResult: this.collectorCommandService.executeDiscoverDevices(collectorId)
    };

    this.discoverDevicesExecutions.set(collectorId, newExecution);

    return newExecution;
  }

  public discoverDeviceById(collectorId: CollectorId, bacnetDeviceId: BacnetDeviceId): DiscoverDeviceByIdExecution {

    const existingExecution = this.findDiscoverDeviceByIdExecution(collectorId);
    if (existingExecution) {
      return existingExecution;
    }

    const newExecution: DiscoverDeviceByIdExecution = {
      collectorId: collectorId,
      bacnetDeviceId: bacnetDeviceId,
      commandResult: this.collectorCommandService.executeDiscoverDeviceById(collectorId, bacnetDeviceId)
    };

    this.discoverDeviceByIdExecutions.set(collectorId, newExecution);

    return newExecution;
  }

  public getAllDeviceObjectProperties(collectorId: CollectorId, deviceId: DeviceId, bacnetDeviceId: BacnetDeviceId): GetAllDeviceObjectPropertiesExecution {

    const existingExecution = this.getAllDeviceObjectPropertiesExecutions.get(deviceId);
    if (existingExecution) {
      return existingExecution;
    }

    const newExecution: GetAllDeviceObjectPropertiesExecution = {
      collectorId: collectorId,
      deviceId: deviceId,
      commandResult: this.collectorCommandService.executeGetAllDeviceObjectProperties(collectorId, bacnetDeviceId)
    };

    this.getAllDeviceObjectPropertiesExecutions.set(deviceId, newExecution);

    return newExecution;
  }
}
